import { Box, Card, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import Swal from "sweetalert2";

import { wrapper } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";

import CampaignEdit from "pages/components/Campaigns/CampaignEdit";

import campaignAPI from "services/api/campaign";
import groupAPI from "services/api/groups";
import sitesAPI from "services/api/sites";
import gtfsAPI from "services/api/gtfs";

import { formatCampaignDataForSubmit, formatCampaignDataForForm } from "utils/campaigns";
import SoftBox from "components/SoftComponents/SoftBox";
import { loadingScreen } from "utils/wrapper";
import { useEffect } from "react";

function CampaignView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, appearance } = controller;

    useEffect(() => {
        if (!map.current) return;
        disableMap(map, user, selectedOrg);
    }, [selectedOrg]);

    campaignAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // 1. Get ID and determine if we are in "Create" mode
    const match = useMatch('campaigns/:id');
    const id = match?.params?.id;
    const isNew = id === 'new';

    // Grab the typeId from the URL (e.g., ?typeId=64a1b2c3...)
    const [searchParams] = useSearchParams();
    const typeIdFromUrl = searchParams.get('typeId');

    // 2. Fetch Campaign (or return a blank template if 'new')
    const campaign = useQuery({
        queryKey: ['campaigns', id, typeIdFromUrl, selectedOrg],
        queryFn: async () => {
            if (isNew) {
                // Return a blank template with the correct campaignTypeId AND new media fields
                return formatCampaignDataForForm({
                    name: "",
                    campaignTypeId: typeIdFromUrl || "",
                    enabled: true,
                    deviceFilter: [],
                    scheduleType: "alwaysOn",
                    scheduling: {},
                    mediaType: "media",
                    contentId: null,
                    formData: null
                }, user.organisations.find(row => row.organisation_id == selectedOrg).timezone);
            }

            try {
                let response = await campaignAPI.getCampaign(id);
                return formatCampaignDataForForm(response, user.organisations.find(row => row.organisation_id == selectedOrg).timezone);
            }
            catch (e) {
                console.error("Failed to fetch campaign:", e);
                navigate('/campaigns', { replace: true });
                throw e;
            }
        },
        staleTime: isNew ? 0 : (5 * 60 * 1000),
    });

    const tags = useQuery({
        queryKey: ['getAssignedTags', selectedOrg],
        queryFn: async () => {
            return await gtfsAPI.getAssignedTags();
        }
    });

    const deviceGroups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const campaignTypes = useQuery({
        queryKey: ['campaignTypes', selectedOrg],
        queryFn: async () => campaignAPI.getCampaignTypes(),
        staleTime: 5 * 60 * 1000,
    });

    const saveForm = async (data) => {
        try {
            const formattedData = formatCampaignDataForSubmit(data, user.organisations.find(row => row.organisation_id == selectedOrg).timezone);

            if (isNew) {
                const newCampaign = await campaignAPI.createCampaign(formattedData);
                Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Success</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Campaign created successfully</div>`, "success");
                queryClient.invalidateQueries({ queryKey: ['campaigns', selectedOrg] });
                navigate(`/campaigns/${newCampaign._id}`, { replace: true });
            } else {
                await campaignAPI.saveCampaign(formattedData._id, formattedData);
                Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Success</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Campaign updated successfully</div>`, "success");
                queryClient.invalidateQueries({ queryKey: ['campaigns', selectedOrg] });
                queryClient.invalidateQueries({ queryKey: ['campaigns', id, selectedOrg] });
                queryClient.invalidateQueries({ queryKey: ['campaigns', id, typeIdFromUrl, selectedOrg] })
            }
        }
        catch (error) {
            console.error("Save error:", error);
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Failed to save the campaign.</div>`, "error");
        }
    };

    if (campaign.isLoading || campaignTypes.isLoading || deviceGroups.isLoading || tags.isLoading) {
        return loadingScreen(controller, true);
    }

    if (campaign.isError || !campaign.data || !campaignTypes.data || !tags.data) {
        console.log(campaign.isError, !campaign.data, !campaignTypes.data, !tags.data)
        return wrapper(<Box p={3}>Error loading campaign data.</Box>, controller);
    }

    // GROUPS ADDITION
    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
        >
            <CampaignEdit
                campaign={campaign.data}
                campaignTypes={campaignTypes.data}
                tagOptions={{tags: tags.data, deviceGroups: deviceGroups.data}}
                saveForm={saveForm}
            />

        </SoftBox>
    ), controller);
}

CampaignView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
}

export default CampaignView;